import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Keyboard, Mic, MicOff, Phone, Volume2, VolumeX, X } from "lucide-react";
import { Action, canReact, followupsOf } from "./AuraChat";
import { useAura } from "./AuraProvider";
import FollowUps from "./FollowUps";
import MessageActions from "./MessageActions";
import SimmiMarkdown from "./SimmiMarkdown";
import SimmiStatus from "./SimmiStatus";
import { useDictation } from "./useDictation";

// Simmi's conversation in Docket's style: a compact dark card beside the avatar showing what Simmi says (the
// newest line at the bottom, earlier ones scrolling up and fading), and Docket's control pill under it:
//   mic · hang up · › — which expands to ‹ · keyboard · speaker.
// The keyboard turns the pill into an "Ask a follow-up" input with ✕. Typing never interrupts Simmi; a message
// sent while she's speaking is delivered as soon as she finishes.
//   mode: "call" (voice call running) | "text" (typing, no call)

const REVEAL_MS = 45; // voice off: new replies write themselves out, per word/space token
const revealed = new Set(); // message ids already shown in full (so re-renders don't retype them)
const STICK_PX = 60; // within this distance of the bottom, the card follows new lines

const Typewriter = ({ id, text, onProgress, onNavigate }) => {
  const tokens = useMemo(() => text.split(/(\s+)/), [text]);
  const [count, setCount] = useState(revealed.has(id) ? tokens.length : 0);

  useEffect(() => {
    if (revealed.has(id)) return undefined;
    const timer = setInterval(() => {
      setCount((n) => {
        if (n + 1 >= tokens.length) {
          clearInterval(timer);
          revealed.add(id);
          return tokens.length;
        }
        return n + 1;
      });
    }, REVEAL_MS);
    return () => clearInterval(timer);
  }, [id, tokens]);

  useEffect(() => {
    onProgress?.();
  }, [count, onProgress]);

  return <SimmiMarkdown text={tokens.slice(0, count).join("")} onNavigate={onNavigate} />;
};

const TypingDots = () => (
  <span className="inline-flex items-center gap-1.5 py-1" aria-label="Simmi is thinking">
    {[0, 1, 2].map((i) => (
      <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/70" style={{ animationDelay: `${i * 0.15}s` }} />
    ))}
  </span>
);

const PillButton = ({ onClick, label, active = false, danger = false, children }) => (
  <button
    onClick={onClick}
    aria-label={label}
    title={label}
    className={`flex h-9 w-9 items-center justify-center rounded-[11px] border transition-colors ${
      danger
        ? "border-[#9b2335]/60 bg-[#5c1622] text-[#f0555f] hover:bg-[#6e1a28]"
        : active
          ? "border-white bg-white text-black"
          : "border-white/10 bg-[#2a2a30] text-white hover:bg-[#34343b]"
    }`}
  >
    {children}
  </button>
);

const AuraDockText = ({ onNavigate, mode = "text" }) => {
  const {
    messages,
    sending,
    send,
    setOpen,
    setHidden,
    hasConversation,
    speakingId,
    caption,
    noteTyping,
    callState,
    muted,
    toggleMute,
    hangUp,
    outputMuted,
    setOutputMuted,
    status,
    lang,
    callActive,
    t,
    typeRequestAt,
  } = useAura();
  const live = mode === "call";
  const [draft, setDraft] = useState("");
  const [queued, setQueued] = useState(null); // sent while Simmi was speaking: delivered when she finishes
  const [expanded, setExpanded] = useState(false); // pill: › reveals keyboard + speaker
  const [inputOpen, setInputOpen] = useState(mode === "text");
  // Simmi asked them to type (their phone number, email…): open the typing box, ready to type into.
  useEffect(() => {
    if (typeRequestAt) setInputOpen(true);
  }, [typeRequestAt]);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const stickRef = useRef(true);
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  const dictation = useDictation({ draft, setDraft, lang, disabled: callActive || !inputOpen });

  // Simmi is mid-reply: typing is fine, but the message waits until she's done.
  const busy = sending || Boolean(speakingId) || (live && ["speaking", "thinking", "idle"].includes(callState));

  useEffect(() => {
    if (queued && !busy) {
      stickRef.current = true;
      send(queued);
      setQueued(null);
    }
  }, [queued, busy, send]);

  const [faded, setFaded] = useState(false); // earlier lines are hidden above: fade the top edge
  const measure = () => {
    const el = listRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_PX;
    setFaded(el.scrollTop > 4);
  };
  const measureRef = useRef(measure);
  measureRef.current = measure;
  const follow = useRef(() => {
    const el = listRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
    measureRef.current();
  }).current;

  useEffect(() => {
    follow();
  }, [messages, sending, caption, follow]);

  useEffect(() => {
    if (inputOpen) inputRef.current?.focus();
  }, [inputOpen]);

  // Ctrl/⌘+K (see AuraProvider) opens the input and puts the cursor in it.
  useEffect(() => {
    const focus = () => {
      setInputOpen(true);
      inputRef.current?.focus();
    };
    window.addEventListener("simmi:focus", focus);
    return () => window.removeEventListener("simmi:focus", focus);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    dictation.stop();
    setDraft("");
    if (busy) setQueued(text);
    else {
      stickRef.current = true;
      send(text);
    }
  };

  const closeInput = () => {
    if (live) setInputOpen(false);
    else {
      setOpen(false); // typing without a call: ✕ hides the agent
      setHidden(true);
    }
  };

  const hearing = live && callState === "listening" && !speakingId && caption;

  // A reply line: the live caption while Simmi is speaking it (in step with her voice), the full text once
  // spoken, else — voice off — the newest reply writes itself out.
  // A reply that streamed onto the page is already written out: while it's then spoken, it stays as it is.
  const replyBody = (m) => {
    if (m.id === speakingId && !m.streamed) return caption || <TypingDots />;
    if (m.voiced || m.streamed || m.id !== lastAssistant?.id) return <SimmiMarkdown text={m.text} onNavigate={onNavigate} />;
    return <Typewriter id={m.id} text={m.text} onProgress={follow} onNavigate={onNavigate} />;
  };
  const chips = !busy && lastAssistant?.kind !== "end" && messages[messages.length - 1] === lastAssistant ? followupsOf(lastAssistant) : [];

  return (
    <div className="relative flex w-[calc(100vw-24px)] flex-col sm:mb-8 sm:w-[min(300px,calc(100vw-24px))]" data-simmi>
      {/* What Simmi says — one Docket-style card that reaches back behind the avatar; scroll up for earlier lines */}
      <div className="aura-convo">
        {/* Docket's step: x=24 is the card's right edge; the fill hides the card's own corner beneath it */}
        <svg className="aura-convo-step" viewBox="0 0 88 20" aria-hidden="true">
          <path d="M0 0.5 H8 C24 0.5 22 12.5 40 12.5 H88 V20 H0 Z" fill="var(--convo-bg)" />
          <path d="M0 0.5 H8 C24 0.5 22 12.5 40 12.5 H88" fill="none" stroke="var(--convo-line)" strokeWidth="1" />
        </svg>
        <div
          ref={listRef}
          onScroll={measure}
          className={`aura-convo-scroll ${faded ? "aura-convo-scroll--faded" : ""}`}
          aria-live="polite"
        >
          {messages.map((m) =>
            m.role === "user" ? (
              // The visitor's question: quieter, right-aligned, in the same card
              <p key={m.id} className="aura-line aura-line--user aura-msg">
                {m.text}
              </p>
            ) : (
              <div key={m.id} className="aura-msg">
                {(m.text || m.id === speakingId) && (
                  <div className={`aura-line ${m.kind === "end" ? "text-white/65" : ""}`}>{replyBody(m)}</div>
                )}
                {m.id !== speakingId && m.actions?.some((a) => a.type !== "followups") && (
                  <div className="mt-2.5 flex flex-col items-start gap-2">
                    {m.actions.map((a, i) => (
                      <Action key={i} action={a} onNavigate={onNavigate} message={m} />
                    ))}
                  </div>
                )}
                {/* Copy / 👍 / 👎 on the latest answer only, to keep the card compact */}
                {m.id === lastAssistant?.id && !busy && canReact(m) && <MessageActions message={m} className="-ml-1.5 mt-1" />}
              </div>
            ),
          )}
          {/* In a call, the visitor's words appear as they speak */}
          {hearing && <p className="aura-line aura-line--user italic">{caption}</p>}
          {queued && <p className="aura-line aura-line--user opacity-60">{queued}</p>}
          {sending && !speakingId && status && <SimmiStatus statusKey={status} t={t} />}
          <FollowUps items={chips} />
          {/* Starter questions, inside the card under Simmi's greeting */}
          {!hasConversation && mode === "text" && (
            <div className="flex flex-wrap gap-1.5">
              {t.starters.slice(0, 3).map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  className="rounded-full border border-white/15 px-3 py-1.5 text-[12px] text-white/85 transition-colors hover:border-white/35 hover:text-white"
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {inputOpen ? (
        /* "Ask a follow-up" — Docket's input pill with ✕ */
        <form onSubmit={submit} className="aura-pill aura-pill--docked flex items-center gap-2 rounded-full py-1.5 pl-5 pr-1.5">
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              noteTyping();
            }}
            placeholder={dictation.recording ? t.dictating : queued ? t.sendingWhenDone : t.askFollowUp}
            maxLength={2000}
            aria-label="Ask Simmi a follow-up"
            className="min-w-0 flex-1 bg-transparent text-[14px] text-white placeholder:text-white/45 focus:outline-none"
          />
          {dictation.supported && !live && (
            // Dictation into the box: hold to talk, or tap to start and stop
            <button
              type="button"
              {...dictation.buttonProps}
              aria-label={t.dictate}
              title={dictation.denied ? t.micDenied : t.dictate}
              className={`relative flex h-9 w-9 shrink-0 touch-none select-none items-center justify-center rounded-full transition-colors ${
                dictation.recording ? "bg-rose-500/25 text-rose-300" : dictation.denied ? "text-rose-300/70" : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            >
              {dictation.recording && <span className="absolute inset-0 animate-ping rounded-full border border-rose-400/70" />}
              <Mic className="relative h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={closeInput}
            aria-label={live ? "Back to the call controls" : "Close"}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/15"
          >
            <X className="h-4 w-4" />
          </button>
        </form>
      ) : (
        /* Docket's call pill: mic · hang up · › (expands to ‹ · keyboard · speaker) */
        <div className="aura-pill aura-pill--docked flex w-fit items-center gap-1.5 rounded-[20px] p-1.5">
          <PillButton onClick={toggleMute} label={muted ? "Unmute microphone" : "Mute microphone"} active={muted}>
            {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </PillButton>
          <PillButton onClick={hangUp} label="End conversation" danger>
            {/* the plain hang-up handset (no strike-through), as on Docket */}
            <Phone className="h-4 w-4 rotate-[135deg]" fill="currentColor" strokeWidth={1.5} />
          </PillButton>
          <button
            onClick={() => setExpanded((x) => !x)}
            aria-label={expanded ? "Fewer controls" : "More controls"}
            aria-expanded={expanded}
            className="flex h-9 w-6 items-center justify-center text-white/55 transition-colors hover:text-white"
          >
            {expanded ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          {expanded && (
            <>
              <PillButton onClick={() => setInputOpen(true)} label="Type a follow-up">
                <Keyboard className="h-4 w-4" />
              </PillButton>
              <PillButton
                onClick={() => setOutputMuted(!outputMuted)}
                label={outputMuted ? "Unmute Simmi's voice" : "Mute Simmi's voice"}
                active={outputMuted}
              >
                {outputMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </PillButton>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AuraDockText;
